import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  onSnapshot,
  orderBy,
  Timestamp,
  getDocs,
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import type { Lead } from '@/types';
import { normalizeLoanStatus } from '@/types';
import { normalizePhoneNumber } from '@/lib/constants';
import { logger } from '@/lib/logger';

// Collection reference
const leadsCollection = collection(db, 'leads');

/**
 * Convert Firestore timestamp to Date
 */
const convertTimestamp = (timestamp: Date | Timestamp | undefined): Date => {
  if (timestamp instanceof Date) return timestamp;
  if (timestamp && typeof timestamp === 'object' && 'toDate' in timestamp) return timestamp.toDate();
  return new Date();
};

/**
 * Convert Firestore document to Lead object
 */
const convertFirestoreToLead = (id: string, data: Record<string, unknown>): Lead => {
  return {
    ...data,
    id,
    status: normalizeLoanStatus(data.status),
    createdAt: convertTimestamp(data.createdAt as Date | Timestamp),
    updatedAt: convertTimestamp(data.updatedAt as Date | Timestamp),
    dateOfBirth: data.dateOfBirth ? convertTimestamp(data.dateOfBirth as Date | Timestamp) : undefined,
    salesConsiderationDate: data.salesConsiderationDate ? convertTimestamp(data.salesConsiderationDate as Date | Timestamp) : undefined,
    timeline: ((data.timeline as Array<Record<string, unknown>>) || []).map((item: Record<string, unknown>) => ({
      ...item,
      createdAt: convertTimestamp(item.createdAt as Date | Timestamp),
      dueDate: item.dueDate ? convertTimestamp(item.dueDate as Date | Timestamp) : undefined,
    })),
    disbursements: ((data.disbursements as Array<Record<string, unknown>>) || []).map((item: Record<string, unknown>) => ({
      ...item,
      disbursementDate: convertTimestamp(item.disbursementDate as Date | Timestamp),
      salesConsiderationDate: item.salesConsiderationDate ? convertTimestamp(item.salesConsiderationDate as Date | Timestamp) : undefined,
      createdAt: convertTimestamp(item.createdAt as Date | Timestamp),
      updatedAt: item.updatedAt ? convertTimestamp(item.updatedAt as Date | Timestamp) : undefined,
    })),
    loginRecords: ((data.loginRecords as Array<Record<string, unknown>>) || []).map((item: Record<string, unknown>) => ({
      ...item,
      createdAt: convertTimestamp(item.createdAt as Date | Timestamp),
      updatedAt: item.updatedAt ? convertTimestamp(item.updatedAt as Date | Timestamp) : undefined,
    })),
    questionnaireData: data.questionnaireData ? {
      ...(data.questionnaireData as Record<string, unknown>),
      lastUpdated: convertTimestamp((data.questionnaireData as Record<string, unknown>).lastUpdated as Date | Timestamp),
    } : undefined,
  } as Lead;
};

/**
 * Subscribe to leads visible to the current user (real-time updates).
 *
 * For admin / business_head the Firestore rules allow a broad read, so we
 * fall back to a simple orderBy query.
 *
 * For every other active user we run TWO parallel listeners that match the
 * security-rule conditions and merge / deduplicate the results:
 *   1. Leads in the same office  → where('officeId', '==', officeId)
 *   2. Leads shared with the user → where('sharedWith', 'array-contains', userId)
 */
export const subscribeToLeads = (
  callback: (leads: Lead[]) => void,
  options?: { userId?: string; officeId?: string; isBH?: boolean }
): (() => void) => {
  // BH / admin path – broad query allowed by rules
  if (options?.isBH) {
    const q = query(leadsCollection, orderBy('createdAt', 'desc'));
    const unsub = onSnapshot(
      q,
      (snapshot) => {
        const leads: Lead[] = [];
        snapshot.forEach((d) => leads.push(convertFirestoreToLead(d.id, d.data())));
        callback(leads);
      },
      (error) => {
        console.error('Error subscribing to leads (BH):', error);
        callback([]);
      },
    );
    return unsub;
  }

  // Regular user path – two parallel queries merged
  const userId = options?.userId;

  if (!userId) {
    logger.warn('subscribeToLeads called without userId for non-BH user');
    callback([]);
    return () => {};
  }

  // Query 1: leads created by this user
  const createdByQuery = query(
    leadsCollection,
    where('createdBy', '==', userId),
    orderBy('createdAt', 'desc'),
  );

  // Query 2: leads shared with this user (may be cross-office)
  const sharedQuery = query(
    leadsCollection,
    where('sharedWith', 'array-contains', userId),
    orderBy('createdAt', 'desc'),
  );

  let createdLeads: Lead[] = [];
  let sharedLeads: Lead[] = [];

  const mergeAndNotify = () => {
    const map = new Map<string, Lead>();
    [...createdLeads, ...sharedLeads].forEach((lead) => map.set(lead.id, lead));
    const merged = Array.from(map.values()).sort(
      (a, b) => b.createdAt.getTime() - a.createdAt.getTime(),
    );
    callback(merged);
  };

  const unsubCreated = onSnapshot(
    createdByQuery,
    (snapshot) => {
      createdLeads = snapshot.docs.map((d) => convertFirestoreToLead(d.id, d.data()));
      mergeAndNotify();
    },
    (error) => {
      console.error('Error subscribing to created leads:', error);
      createdLeads = [];
      mergeAndNotify();
    },
  );

  const unsubShared = onSnapshot(
    sharedQuery,
    (snapshot) => {
      sharedLeads = snapshot.docs.map((d) => convertFirestoreToLead(d.id, d.data()));
      mergeAndNotify();
    },
    (error) => {
      console.error('Error subscribing to shared leads:', error);
      sharedLeads = [];
      mergeAndNotify();
    },
  );

  return () => {
    unsubCreated();
    unsubShared();
  };
};

/**
 * Add a new lead
 */
export const addLead = async (lead: Omit<Lead, 'id'>): Promise<string> => {
  try {
    // Normalize phone number before saving
    const normalizedLead: Record<string, unknown> = {
      ...lead,
      mobile: normalizePhoneNumber(lead.mobile),
      
      // Boolean fields with defaults (ensure consistency in Firestore)
      isBT: lead.isBT ?? false,
      isTopUp: lead.isTopUp ?? false,
      
      // Timestamp conversions
      createdAt: Timestamp.fromDate(lead.createdAt),
      updatedAt: Timestamp.fromDate(lead.updatedAt),
      dateOfBirth: lead.dateOfBirth ? Timestamp.fromDate(lead.dateOfBirth) : null,
      salesConsiderationDate: lead.salesConsiderationDate ? Timestamp.fromDate(lead.salesConsiderationDate) : null,
      
      // Timeline with timestamp conversion
      timeline: lead.timeline.map(item => ({
        ...item,
        createdAt: Timestamp.fromDate(item.createdAt),
        dueDate: item.dueDate ? Timestamp.fromDate(item.dueDate) : null,
      })),
    };

    // Remove undefined fields (Firestore doesn't accept undefined)
    Object.keys(normalizedLead).forEach(key => {
      if (normalizedLead[key] === undefined) {
        delete normalizedLead[key];
      }
    });

    const docRef = await addDoc(leadsCollection, normalizedLead);
    logger.info('Lead created successfully', { leadId: docRef.id, mobile: lead.mobile, createdBy: lead.createdBy });
    return docRef.id;
  } catch (error) {
    logger.error('Failed to add lead', error, { mobile: lead.mobile, firstName: lead.firstName, lastName: lead.lastName });
    throw new Error(`Failed to add lead: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};

/**
 * Recursively remove undefined values from an object
 * Firestore doesn't accept undefined values
 */
const removeUndefinedDeep = (obj: any): any => {
  if (obj === null || obj === undefined) {
    return null;
  }
  
  if (Array.isArray(obj)) {
    return obj.map(item => removeUndefinedDeep(item));
  }
  
  if (typeof obj === 'object' && !(obj instanceof Date) && !(obj instanceof Timestamp)) {
    const cleaned: any = {};
    Object.keys(obj).forEach(key => {
      const value = obj[key];
      if (value !== undefined) {
        cleaned[key] = removeUndefinedDeep(value);
      }
    });
    return cleaned;
  }
  
  return obj;
};

/**
 * Update an existing lead
 */
export const updateLead = async (
  leadId: string,
  updates: Partial<Lead>
): Promise<void> => {
  try {
    const leadRef = doc(db, 'leads', leadId);
    
    const updatesToApply: Record<string, unknown> = {
      ...updates,
      updatedAt: Timestamp.now(),
    };

    // Boolean fields with defaults (if being updated)
    if ('isBT' in updates) {
      updatesToApply.isBT = updates.isBT ?? false;
    }
    if ('isTopUp' in updates) {
      updatesToApply.isTopUp = updates.isTopUp ?? false;
    }

    // Normalize phone if being updated
    if (updates.mobile) {
      updatesToApply.mobile = normalizePhoneNumber(updates.mobile);
    }

    // Convert Date objects to Timestamps
    if (updates.dateOfBirth) {
      updatesToApply.dateOfBirth = Timestamp.fromDate(updates.dateOfBirth);
    }
    if (updates.salesConsiderationDate) {
      updatesToApply.salesConsiderationDate = Timestamp.fromDate(updates.salesConsiderationDate);
    }
    if (updates.timeline) {
      updatesToApply.timeline = updates.timeline.map(item => ({
        ...item,
        createdAt: Timestamp.fromDate(item.createdAt),
        dueDate: item.dueDate ? Timestamp.fromDate(item.dueDate) : null,
      }));
    }
    if (updates.disbursements) {
      updatesToApply.disbursements = updates.disbursements.map(item => ({
        ...item,
        disbursementDate: Timestamp.fromDate(item.disbursementDate),
        salesConsiderationDate: item.salesConsiderationDate ? Timestamp.fromDate(item.salesConsiderationDate) : null,
        createdAt: Timestamp.fromDate(item.createdAt),
        updatedAt: item.updatedAt ? Timestamp.fromDate(item.updatedAt) : null,
      }));
    }
    if (updates.questionnaireData) {
      updatesToApply.questionnaireData = {
        ...updates.questionnaireData,
        lastUpdated: updates.questionnaireData.lastUpdated && updates.questionnaireData.lastUpdated instanceof Date
          ? Timestamp.fromDate(updates.questionnaireData.lastUpdated)
          : Timestamp.now(),
      };
    }

    // Deep clean: Remove all undefined fields (Firestore doesn't accept undefined values)
    const cleanedUpdates = removeUndefinedDeep(updatesToApply);

    await updateDoc(leadRef, cleanedUpdates);
    logger.info('Lead updated successfully', { leadId, fieldsUpdated: Object.keys(updates).length });
  } catch (error) {
    logger.error('Failed to update lead', error, { leadId });
    throw new Error(`Failed to update lead: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};

/**
 * Delete a lead
 */
export const deleteLead = async (leadId: string): Promise<void> => {
  try {
    const leadRef = doc(db, 'leads', leadId);
    await deleteDoc(leadRef);
    logger.info('Lead deleted successfully', { leadId });
  } catch (error) {
    logger.error('Failed to delete lead', error, { leadId });
    throw new Error(`Failed to delete lead: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};

/**
 * Check if a lead with the given phone number already exists.
 *
 * Uses an officeId filter so the query satisfies Firestore security rules
 * for non-admin users. For BH/admin, officeId can be omitted.
 */
export const checkLeadExists = async (
  phone: string,
  excludeId?: string,
  officeId?: string,
): Promise<boolean> => {
  try {
    const normalizedPhone = normalizePhoneNumber(phone);

    // Build query – add officeId constraint when provided so non-BH users
    // can execute this. BH/admin omit officeId (their rule allows broad reads).
    const constraints = [where('mobile', '==', normalizedPhone)];
    if (officeId) {
      constraints.push(where('officeId', '==', officeId));
    }

    const q = query(leadsCollection, ...constraints);

    const snapshot = await getDocs(q);
    
    if (snapshot.empty) {
      return false;
    }
    
    // If excludeId is provided, check if the found lead is not the same one
    if (excludeId) {
      return snapshot.docs.some(doc => doc.id !== excludeId);
    }
    
    return true;
  } catch (error) {
    logger.error('Failed to check lead existence', error, { phone, excludeId });
    return false;
  }
};

/**
 * Get all banks from Firebase
 */
export const getAllBanks = async (): Promise<Array<{ id: string; name: string; logo?: string }>> => {
  try {
    const banksCollection = collection(db, 'banks');
    const q = query(banksCollection, orderBy('name', 'asc'));
    const snapshot = await getDocs(q);
    
    const banks: Array<{ id: string; name: string; logo?: string }> = [];
    snapshot.forEach((doc) => {
      const data = doc.data();
      banks.push({
        id: doc.id,
        name: data.name,
        logo: data.logo,
      });
    });
    
    return banks;
  } catch (error) {
    logger.error('Failed to fetch banks', error);
    return [];
  }
};
